import {
  CategoryFormDataType,
  CategoryMappingType,
  ClassifierFormDataType,
  ClassifierMappingsType,
  getDayjs,
  getDatePeriodString,
  isInDateRange,
  PeriodArray,
  TimePeriodType,
  ValidationMessage,
  ValidationMessagesMap,
} from '@atlas-frontend/sdk';
import { TFunction } from 'i18next';

function addCategories(
  map: Map<number, CategoryFormDataType>,
  categories?: CategoryFormDataType[],
  classifier?: ClassifierFormDataType,
) {
  categories?.forEach((category) => {
    if (category.id == null) return;

    const cat = {
      ...category,
    };
    // If category has no validity period, use classifier validity period
    if (cat.validPeriod) {
      if (!cat.validPeriod[0]) {
        cat.validPeriod[0] = classifier?.validPeriod[0] ?? null;
      }
      if (!cat.validPeriod[1]) {
        cat.validPeriod[1] = classifier?.validPeriod[1] ?? null;
      }
    }
    map.set(category.id, category);
  });
}

function addClassifierCategories(
  map: Map<number, CategoryFormDataType>,
  classifier?: ClassifierFormDataType,
) {
  addCategories(map, classifier?.categories, classifier);
  addCategories(map, classifier?.categoryGroups, classifier);
  addCategories(map, classifier?.combinedGroups, classifier);
}

function isInRange(mapping: CategoryMappingType, period?: TimePeriodType) {
  if (period) {
    const categoryPeriod = [
      getDayjs(period[0]),
      getDayjs(period[1]),
    ] as PeriodArray;
    const validPeriod = [
      getDayjs(mapping.validPeriod?.[0] ?? period[0]),
      getDayjs(mapping.validPeriod?.[1] ?? period[1]),
    ] as PeriodArray;
    return isInDateRange(categoryPeriod, validPeriod);
  }

  return true;
}

function validatePeriod(
  mapping: CategoryMappingType,
  categories: Map<number, CategoryFormDataType>,
  isTo: boolean,
  getErrorMessage: TFunction,
): ValidationMessage | undefined {
  const id = isTo ? mapping.categoryIdTo : mapping.categoryIdFrom;
  if (id) {
    const category = categories.get(id);
    const period = category?.validPeriod;
    if (category && period && !isInRange(mapping, period)) {
      const args = {
        categoryPeriod: getDatePeriodString(period[0], period[1]),
      };
      return isTo
        ? {
            message: getErrorMessage('alert.error.code.overlap', args),
            type: 'error',
          }
        : {
            message: getErrorMessage('alert.warning.overlap', args),
            type: 'warning',
          };
    }
  }

  return undefined;
}

export function validateMapping(
  dto: ClassifierMappingsType,
  getErrorMessage: TFunction,
  classifierFrom?: ClassifierFormDataType,
  classifierTo?: ClassifierFormDataType,
): ValidationMessagesMap {
  const messages = new ValidationMessagesMap();
  if (dto.categoryMappings.length === 0) {
    messages.append(
      'code',
      getErrorMessage('alert.warning.no-mappings'),
      'warning',
    );
  } else {
    const categories = new Map<number, CategoryFormDataType>();
    addClassifierCategories(categories, classifierFrom);
    addClassifierCategories(categories, classifierTo);

    dto.categoryMappings.forEach((mapping, index) => {
      const name = `categoryMappings.${index}.validPeriod`;
      [
        validatePeriod(mapping, categories, false, getErrorMessage),
        validatePeriod(mapping, categories, true, getErrorMessage),
      ].forEach((msg?: ValidationMessage) => {
        if (msg) {
          messages.append(name, msg.message, msg.type);
        }
      });
    });
  }

  return messages;
}
